import React from 'react';
import { useParams, Link } from 'react-router';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { news } from './Home/data/news';
import { events } from './Home/data/events';

export const NewsDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const newsItem = news.find((n) => n.slug === slug);

  if (!newsItem) {
    return (
      <>
        <PageHero
          title="News Not Found"
          subtitle="The news article you're looking for doesn't exist."
          breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'News', path: '/news' }, { label: 'Not Found' }]}
        />
        <Section>
          <Card>
            <p style={{ color: 'var(--muted)', textAlign: 'center' }}>
              <Link to="/news" style={{ color: 'var(--g1)', textDecoration: 'none' }}>
                Return to News
              </Link>
            </p>
          </Card>
        </Section>
      </>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const relatedEvents = newsItem.relatedEvents
    ? events.filter((e) => newsItem.relatedEvents?.includes(e.slug))
    : [];

  return (
    <>
      <PageHero
        title={newsItem.title}
        subtitle={`${formatDate(newsItem.date)} • ${newsItem.tag}`}
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'News', path: '/news' },
          { label: newsItem.title },
        ]}
      />

      <Section>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '32px',
          }}
          className="newsDetailGrid"
        >
          <div style={{ gridColumn: 'span 2' }}>
            <div
              style={{
                color: 'var(--text)',
                lineHeight: 1.8,
                fontSize: '16px',
              }}
            >
              {newsItem.content.split('\n\n').map((paragraph, idx) => (
                <p key={idx} style={{ marginBottom: '20px' }}>
                  {paragraph}
                </p>
              ))}
            </div>

            {newsItem.keyTakeaways && newsItem.keyTakeaways.length > 0 && (
              <Card style={{ marginTop: '32px', backgroundColor: 'var(--panel)' }}>
                <h3
                  style={{
                    fontSize: '22px',
                    fontWeight: 600,
                    color: 'var(--text)',
                    marginBottom: '20px',
                  }}
                >
                  Key Takeaways
                </h3>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {newsItem.keyTakeaways.map((takeaway, idx) => (
                    <li
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        color: 'var(--text)',
                        fontSize: '16px',
                        lineHeight: 1.6,
                      }}
                    >
                      <span
                        style={{
                          color: 'var(--g1)',
                          marginTop: '4px',
                          flexShrink: 0,
                        }}
                      >
                        ✓
                      </span>
                      {takeaway}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          <div>
            <Card style={{ position: 'sticky', top: '100px' }}>
              <h3
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--text)',
                  marginBottom: '20px',
                }}
              >
                Related Items
              </h3>

              {relatedEvents.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h4
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: 'var(--text)',
                      marginBottom: '12px',
                    }}
                  >
                    Events
                  </h4>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    {relatedEvents.map((event) => (
                      <Link
                        key={event.id}
                        to={`/events/${event.slug}`}
                        style={{
                          padding: '12px',
                          backgroundColor: 'var(--panel)',
                          borderRadius: 'var(--r1)',
                          border: '1px solid var(--line)',
                          textDecoration: 'none',
                          display: 'block',
                        }}
                        className="relatedItem"
                      >
                        <div
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: 'var(--text)',
                            marginBottom: '4px',
                          }}
                        >
                          {event.title}
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: 'var(--muted)',
                          }}
                        >
                          {formatDate(event.date)}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: 'var(--text)',
                    marginBottom: '12px',
                  }}
                >
                  Opportunities
                </h4>
                <Badge status="COMING SOON" />
                <div
                  style={{
                    marginTop: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  {[
                    'Korean wastewater solutions partner search',
                    'Egyptian industrial supplier matchmaking',
                  ].map((opp, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '10px',
                        backgroundColor: 'var(--panel)',
                        borderRadius: 'var(--r1)',
                        border: '1px solid var(--line)',
                        fontSize: '13px',
                        color: 'var(--muted)',
                      }}
                    >
                      {opp}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--line)' }}>
                <Link to="/news" style={{ textDecoration: 'none', display: 'block' }}>
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: 'var(--panel)',
                      borderRadius: 'var(--r1)',
                      border: '1px solid var(--line)',
                      textAlign: 'center',
                      color: 'var(--g1)',
                      fontWeight: 500,
                    }}
                    className="backLink"
                  >
                    ← Back to News
                  </div>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </Section>
    </>
  );
};

